import PropTypes from "prop-types";
import React, { useState } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import NoChartData from "../Shared/NoChartData";

const COLORS = [
  "#e60049",
  "#0bb4ff",
  "#10bf5b",
  "#EA7339",
  "#9b19f5",
  "#ffa300",
  "#dc0ab4",
  "#b3d4ff",
  "#00bfa0",
];

const MAX_LABEL_LENGTH = 10;
const CHART_ASPECT_RATIO = 5.0 / 3.0;
const CHART_INNER_RADIUS = 35;
const CHART_OUTER_RADIUS = 50;
const CHART_ANGLE_PADDING = 3;

const PieChartNew = ({
  data,
  sliceItems = true,
  sliceNumber = 4,
  title,
  showExpectedData = false,
}) => {
  const keys = Object.keys(data);

  let finalKeys = [];
  let finalValues = [];
  // Sort keys by their corresponding values in descending order
  const sortedKeys = keys.sort((a, b) => data[b].int_value - data[a].int_value);
  if (sliceItems) {
    // Determine the top 5 keys and the rest as "Other"
    const topKeys = sortedKeys.slice(0, sliceNumber);
    const otherKeys = sortedKeys.slice(sliceNumber);

    // Calculate the "Other" value
    const otherValue = otherKeys.reduce(
      (acc, key) => acc + data[key].int_value,
      0
    );

    // Create the final keys and values arrays
    finalKeys = otherKeys.length > 0 ? [...topKeys, "Other"] : topKeys;
    finalValues =
      otherKeys.length > 0
        ? [...topKeys.map((key) => data[key].int_value), otherValue]
        : topKeys.map((key) => data[key].int_value);
  } else {
    finalKeys = sortedKeys;
    finalValues = sortedKeys.map((key) => data[key].int_value);
  }

  const [chartData, _setChartData] = useState(
    finalKeys.map((key, index) => ({
      name: key,
      value: finalValues[index],
    }))
  );

  const chartLabelFormatter = (label, value) => {
    const total = finalValues.reduce((acc, val) => acc + val, 0);
    const percentage = ((value / total) * 100).toFixed(2);
    const formattedLabel =
      label.length > MAX_LABEL_LENGTH
        ? label.slice(0, MAX_LABEL_LENGTH) + "..."
        : label;
    return formattedLabel + ": " + percentage + "%";
  };

  const expectedDataLabelColor = (expectedLabel) => {
    switch (expectedLabel) {
      case "green":
        return "text-green-700 dark:text-green-500";
      case "red":
        return "text-red-700 dark:text-red-500";
      case "yellow":
        return "text-yellow-700 dark:text-yellow-500";
      case "blue":
        return "text-blue-700 dark:text-blue-500";
      default:
        return "dark:text-gray-300";
    }
  };

  const expectedDataProgressBarColor = (expectedLabel) => {
    switch (expectedLabel) {
      case "green":
        return "bg-green-600 dark:bg-green-500";
      case "red":
        return "bg-red-600 dark:bg-red-500";
      case "yellow":
        return "bg-yellow-400";
      case "blue":
        return "bg-blue-600";
      default:
        return "bg-gray-600 dark:bg-gray-300";
    }
  };

  const expectedDataPercentage = (expectedPercentage) => {
    return expectedPercentage > 100 ? 100 : expectedPercentage;
  };

  if (
    !chartData ||
    Object.keys(data).length === 0 ||
    Object.values(data).every(
      (value) => (value || { int_value: 0 }).int_value === 0
    )
  ) {
    return <NoChartData />;
  }

  return (
    <div className="bg-white dark:bg-gray-800 px-4 pb-2 pt-4 rounded-lg w-full card-shadow">
      {title && (
        <h3 className="dark:text-white font-semibold text-center text-gray-900 mb-2">
          {title}
        </h3>
      )}
      <ResponsiveContainer
        width="100%"
        aspect={CHART_ASPECT_RATIO}
        className="mb-4"
      >
        <PieChart
          {...{
            overflow: "visible",
          }}
        >
          <Pie
            data={chartData}
            innerRadius={CHART_INNER_RADIUS}
            outerRadius={CHART_OUTER_RADIUS}
            paddingAngle={CHART_ANGLE_PADDING}
            isAnimationActive={true}
            dataKey="value"
            label={(e) => chartLabelFormatter(e.name, e.value)}
            nameKey="name"
          >
            {chartData.map((entry, index) => {
              return (
                <Cell
                  name={entry.name}
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              );
            })}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      {sortedKeys.map((key, _index) => (
        <>
          <p
            className="flex px-2 py-1 items-center rounded text-sm justify-between font-normal text-gray-700 dark:text-gray-400 dark:odd:bg-gray-700 odd:bg-gray-100"
            key={key}
          >
            <span>
              {key}
              {showExpectedData &&
                Object.hasOwn(data[key], "expected_percentage") && (
                  <span
                    className={`text-xs ${expectedDataLabelColor(
                      data[key].expected_label
                    )}`}
                  >
                    {" "}
                    ({data[key].expected_percentage}%)
                  </span>
                )}
              :
            </span>
            {showExpectedData &&
            Object.hasOwn(data[key], "expected_percentage") ? (
              <span className="text-xs">
                {data[key].formatted_value}
                <span>/{data[key].expected_formatted_value}</span>
              </span>
            ) : (
              <span>{data[key].formatted_value}</span>
            )}
          </p>
          {showExpectedData &&
            Object.hasOwn(data[key], "expected_percentage") && (
              <div className="w-fullrounded-full h-1.5 mb-4">
                <div
                  className={`h-1.5 rounded-full ${expectedDataProgressBarColor(
                    data[key].expected_label
                  )}`}
                  style={{
                    width:
                      expectedDataPercentage(data[key].expected_percentage) +
                      "%",
                  }}
                ></div>
              </div>
            )}
        </>
      ))}
    </div>
  );
};

PieChartNew.propTypes = {
  data: PropTypes.object.isRequired,
  expectedData: PropTypes.object,
  sliceItems: PropTypes.bool,
  sliceNumber: PropTypes.number,
  title: PropTypes.string,
  showExpectedData: PropTypes.bool,
};

export default PieChartNew;
